import * as React from 'react';
import {
  FocusToggleIcon,
  LayoutContext,
  UnfocusToggleIcon,
  useCreateLayoutContext,
  useMaybeTrackRefContext,
  usePinnedTracks,
} from '@livekit/components-react';
// import { FocusToggleIcon, UnfocusToggleIcon } from "@livekit/components-react";
import {
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownLeft,
  MinimizeIcon,
  MaximizeIcon,
  PinIcon,
  PinOffIcon,
} from 'lucide-react';
import { useFocusToggle } from '@livekit/components-react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { useTheme } from '@/hooks/use-theme.ts';
import { getAvatarTextColor } from '@/lib/avatar-color';

/** @public */
export interface FocusToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  trackRef?: TrackReferenceOrPlaceholder;
}

/**
 * The `FocusToggle` puts the `ParticipantTile` in focus or removes it from focus.
 * @remarks
 * This component needs to live inside `LayoutContext` to work properly.
 *
 * @example
 * ```tsx
 * <ParticipantTile>
 *   <FocusToggle />
 * </ParticipantTile>
 * ```
 * @public
 */
export const FocusToggle: (
  props: FocusToggleProps & React.RefAttributes<HTMLButtonElement>
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<HTMLButtonElement, FocusToggleProps>(
  function FocusToggle({ trackRef, ...props }: FocusToggleProps, ref) {
    const trackRefFromContext = useMaybeTrackRefContext();
    const { effectiveTheme } = useTheme();
    const containerRef = React.useRef<HTMLButtonElement>(null);

    const { mergedProps, inFocus } = useFocusToggle({
      trackRef: trackRef ?? trackRefFromContext,
      props,
    });

    const mergedRef = React.useCallback(
      (node: HTMLButtonElement) => {
        const setRef = ref as
          | React.MutableRefObject<HTMLButtonElement | null>
          | ((node: HTMLButtonElement | null) => void);
        if (typeof setRef === 'function') {
          setRef(node);
        } else if (setRef) {
          setRef.current = node;
        }
        containerRef.current = node;
      },
      [ref]
    );

    const [iconColor, setIconColor] = React.useState<string>('white');

    React.useEffect(() => {
      const updateColor = () => {
        if (!containerRef.current) return;
        const computedStyle = window.getComputedStyle(containerRef.current);
        const bgColor = computedStyle.getPropertyValue('--tile-bg').trim();
        if (bgColor) {
          const match = bgColor.match(/hsl\(\d+,\s*\d+%,\s*(\d+)%\)/);
          const lightness = match ? Number(match[1]) : 50;
          if (lightness < 55) {
            setIconColor('#ffffff');
          } else {
            setIconColor(getAvatarTextColor(bgColor, effectiveTheme as 'dark' | 'light'));
          }
        }
      };

      updateColor();
      const observer = new MutationObserver(updateColor);
      if (containerRef.current?.parentElement) {
        observer.observe(containerRef.current.parentElement, {
          attributes: true,
          attributeFilter: ['style'],
        });
      }
      return () => observer.disconnect();
    }, [effectiveTheme]);

    return (
      <LayoutContext.Consumer>
        {(layoutContext) =>
          layoutContext !== undefined && (
            <button
              ref={mergedRef}
              {...mergedProps}
              style={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {props.children ? (
                props.children
              ) : (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    borderRadius: '4px',
                    padding: '4px',
                  }}
                >
                  {inFocus ? (
                    <PinOffIcon style={{ color: iconColor }} />
                  ) : (
                    <PinIcon style={{ color: iconColor }} />
                  )}
                </span>
              )}
            </button>
          )
        }
      </LayoutContext.Consumer>
    );
  }
);
